'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useNotifications } from '@/lib/services/notifications';
import {
  RECORDING_PROCESSED_NOTIFICATION_TYPE,
  RECORDING_FAILED_NOTIFICATION_TYPE,
  notifyRecordingReady,
  notifyRecordingFailed
} from '@/lib/services/recordings/recording-completion-toast';

/**
 * Global catch-all for creator-scoped recording completion / failure toasts
 * (LF-3712, FE-9). Mounted once in the authenticated tree so it fires wherever
 * the lawyer is when their recording finishes — the complement to the
 * session page, which fires immediately off its own 5s poll while open.
 *
 * Watches the firm-wide notifications poll (the backend emits a creator-scoped
 * `recording.processed` / `recording.failed` notification with
 * `metadata.recordingId`). Notifications present at mount are treated as the
 * baseline so the historical backlog never toasts; only notifications that
 * arrive on a later poll fire. The shared sessionStorage dedup in
 * `recording-completion-toast` prevents a double toast with the session page.
 *
 * Renders nothing.
 */
export function RecordingCompletionToaster() {
  const router = useRouter();
  const { data } = useNotifications();
  // null until the first successful fetch seeds the baseline of seen ids.
  const seenIdsRef = useRef<Set<number> | null>(null);

  const notifications = data?.notifications;

  useEffect(() => {
    if (!notifications) return;

    if (seenIdsRef.current === null) {
      // First load — seed the baseline, don't toast the existing backlog.
      seenIdsRef.current = new Set(notifications.map((n) => n.id));
      return;
    }

    const seen = seenIdsRef.current;
    for (const notification of notifications) {
      if (seen.has(notification.id)) continue;
      seen.add(notification.id);

      const recordingId = Number(
        (notification.metadata?.recordingId as number | undefined) ??
          notification.entityId
      );
      if (!Number.isFinite(recordingId) || recordingId <= 0) continue;

      const title = notification.metadata?.title as string | undefined;
      const onView = () => router.push(`/scribe/${recordingId}`);

      if (notification.type === RECORDING_PROCESSED_NOTIFICATION_TYPE) {
        notifyRecordingReady({ recordingId, title, onView });
      } else if (notification.type === RECORDING_FAILED_NOTIFICATION_TYPE) {
        notifyRecordingFailed({ recordingId, title, onView });
      }
    }
  }, [notifications, router]);

  return null;
}
